import * as React from 'react';

// components
import Input from '../shared/Input';
import { NavLink } from 'react-router-dom';
import { Alert, Button, Label } from 'reactstrap';

// final form
import { Field, Form } from 'react-final-form';

// networking
import AxiosHelper from 'services/AxiosHelper';
import * as axios from 'axios';
import { AxiosError, AxiosResponse } from 'axios';

// types
import { LoginResponse } from './types';
import Spinner from '../../../components/shared/Spinner';

import createDecorator from 'final-form-focus';
import { handleAxiosErrorWithValidation } from 'util/Misc';
//#region Smo
import { setDefaultTenantIdToSessionAndLocalStorage } from '~/features/tenants/helpers/TenantHelper';
//#endregion Smo

const focusOnErrors = createDecorator();

interface LoginState {
	showLoginButtonLoading: boolean;
	error: AxiosError | null;
	announcementText: string;
	showEntraIdLoginButtonLoading: boolean;
	entraIdEnabled: boolean;
}

interface LoginData {
	entraIdEnabled: boolean;
}

/**
 * Login component
 *
 * @class Login
 * @extends {React.PureComponent<any, LoginState>}
 */
class Login extends React.PureComponent<any, LoginState> {
	private cancelToken: any = null;

	constructor(props: any) {
		super(props);

		this.state = {
			showLoginButtonLoading: false,
			error: null,
			announcementText: '',
			showEntraIdLoginButtonLoading: false,
			entraIdEnabled: false,
		};
	}

	public componentDidMount() {
		// Load login data from the page javascript
		const twoFactorData: LoginData = (window as any).__ROC_LOGIN_DATA__;
		this.setState({ ...this.state, entraIdEnabled: twoFactorData?.entraIdEnabled ?? false });
	}

	public componentWillUnmount() {
		if (this.cancelToken) {
			this.cancelToken();
		}
	}

	public submit = (formValues: any): Promise<void | Record<string, string>> => {
		if (this.cancelToken) {
			this.cancelToken();
		}

		this.setState({
			showLoginButtonLoading: !formValues.isEntraIdLogin,
			showEntraIdLoginButtonLoading: formValues.isEntraIdLogin,
			error: null,
		});

		const searchParams = new URLSearchParams(this.props.location ? this.props.location.search : '');
		const redirectUrl = searchParams.get('ReturnUrl');

		const url = `/ajax/login/?returnUrl=${encodeURIComponent(redirectUrl ?? '')}`;

		this.setState({ announcementText: 'Loading' });

		return AxiosHelper.post(url, formValues, {
			cancelToken: new axios.default.CancelToken((token) => {
				this.cancelToken = token;
			}),
		})
			.then((response: AxiosResponse<LoginResponse>) => {
				//#region Smo
				setDefaultTenantIdToSessionAndLocalStorage(response?.data?.defaultTenantId || '');
				//#endregion Smo
				if (response.data.forceRedirect) {
					window.location.href = response.data.redirectUrl || '/';
				} else if (response.data.success) {
					window.location.href = redirectUrl || '/';
				} else {
					if (this.props.history) {
						this.props.history.push(response.data.redirectUrl);
					}
				}
			})
			.catch((error: AxiosError) => {
				this.setState({ showLoginButtonLoading: false, error });
				return handleAxiosErrorWithValidation(error, 'An error occurred while logging in.');
			})
			.then((promise: any) => {
				this.cancelToken = null;
				return promise;
			});
	};

	public render() {
		return (
			<div className="login-form-wrapper">
				<div className="visually-hidden" role="status" aria-live="assertive" aria-atomic="true">
					{this.state.announcementText}
				</div>
				<Form
					onSubmit={this.submit}
					initialValues={{
						username: '',
						password: '',
						isEntraIdLogin: false,
					}}
					subscription={{ submitting: true, submitError: true }}
					decorators={[focusOnErrors]}
					render={({ handleSubmit, submitError, form }) => (
						<form onSubmit={handleSubmit} className="login-form">
							{submitError ? (
								<div data-testid="login-error">
									<Alert color="danger">{submitError}</Alert>
								</div>
							) : null}
							{/* #region Smo */}
							<Label for="username">Email</Label>
							{/* #endregion Smo */}
							<Field
								name="username"
								type="text"
								autoFocus
								className="login-form__text-input"
								component={Input}
								data-testid="login-username"
							/>
							<Label for="password">Password</Label>
							<Field
								name="password"
								type="password"
								className="login-form__text-input"
								component={Input}
								data-testid="login-password"
							/>

							<div className="login-form__actions">
								<Button
									type="submit"
									disabled={this.state.showLoginButtonLoading}
									color="primary"
									className="login-form__action login-form__submit-btn"
									data-testid="login-submit"
								>
									{this.state.showLoginButtonLoading ? <Spinner color="light" centered /> : 'Login'}
								</Button>
								{this.state.entraIdEnabled && (
									<Button
										type="submit"
										disabled={this.state.showEntraIdLoginButtonLoading}
										color="primary"
										className="login-form__action login-form__submit-btn"
										data-testid="login-submit"
										onClick={() => {
											form.change('isEntraIdLogin', true);
											form.submit();
										}}
									>
										{this.state.showEntraIdLoginButtonLoading ? (
											<Spinner color="light" centered />
										) : (
											'Login with Entra ID (AE)'
										)}
									</Button>
								)}
							</div>
							<div className="login-form__footer">
								<NavLink
									className="login-form__link login-form__action"
									exact
									activeClassName="active"
									to="/account/forgot-password"
								>
									Forgot Password?
								</NavLink>
							</div>
							<div className="login-form__actions" />
						</form>
					)}
				/>
			</div>
		);
	}
}

export default Login;
